import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { editorColors } from "store/editorColors";
import { MaterialIcon } from "components/ui/shared/MaterialIcon";

export const EditorToolbarTray = ({children}) => {
    return (
        <StyledEditorToolbarTray>
            {children}
        </StyledEditorToolbarTray>
    )
}

const StyledEditorToolbarTray = styled.div`
    padding: ${({theme}) => theme.spacing.xs} ${({theme}) => theme.spacing.md};
    position: relative;
    display: flex;
    gap: ${({theme}) => theme.spacing.sm};

    &::before
    {
        position: absolute;
        transform: translateY(-50%);
        content: "";
        left: 0;
        top: 50%;
        width: 1px;
        height: 20px;
        background: ${({theme}) => theme.colors.neutralPrimaryBorder};
    }

    &:first-child::before
    {
        background: none;
        width: 0;
        height: 0;
    }
`

export const EditorToolbarButton = ({icon, active = false, disabled, onClick}) => {
    
    return (
        <StyledEditorToolbarButton
            className={active ? 'isActive' : disabled ? 'isDisabled' : null}
            onClick={onClick}
        >
            {icon}
        </StyledEditorToolbarButton>
    )
}

const StyledEditorToolbarButton = styled.button`
    border: 0;
    outline: 0;
    background: none;
    padding: ${({theme}) => theme.spacing.xs};
    cursor: pointer;
    border-radius: ${({theme}) => theme.cornerRadius.sm};
    display: flex;
    align-items: center;
    justify-content: center;
    transition-duration: 110ms;

    &:hover
    {
        background: rgba(${({theme}) => theme.colors.neutralPrimaryBorderRGB}, 0.75);
    }

    &.isActive
    {
        background: ${({theme}) => theme.colors.neutralPrimaryBorder};
    }

    &.isDisabled
    {
        svg
        {
            color: rgba(${({theme}) => theme.colors.mutedForegroundRGB}, 0.5);
        }

        &:hover
        {
            cursor: not-allowed;
            background: none;
        }
        
    }
`

const DropdownContext = React.createContext(null);

export const EditorToolbarTextDropdown = ({defaultValue, defaultIcon, defaultName, selectedValue, children, onChange}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [maxWidth, setMaxWidth] = useState(null);
    const dropdownContentRef = useRef(null);
    const dropdownHeaderRef = useRef(null);

    const dropdownSelect = (value) => {
        setIsDropdownOpen(false);
        onChange?.(value);
    };

    useEffect(() => {
        const headerEl = dropdownHeaderRef.current;
        const contentRef = dropdownContentRef.current;
        if(!headerEl || !contentRef) return;

        const clickHandler = (e) => {
            if(!e.target) return;

            if(!headerEl.contains(e.target) && !contentRef.contains(e.target)) setIsDropdownOpen(false);
        }

        document.addEventListener("click", clickHandler);

        return () => document.removeEventListener("click", clickHandler);
    }, [isDropdownOpen])

    
    
    useEffect(() => {
        if(dropdownContentRef.current)
        {
            const width = dropdownContentRef.current.getBoundingClientRect().width;
            setMaxWidth(prev => (prev ? Math.max(prev, width) : width));
        }
    }, [isDropdownOpen, children]);

    const currentSelectedValue = useMemo(() => {
        if (!selectedValue)
        {
            return {
                value: defaultValue,
                name: defaultName,
                icon: defaultIcon
            };
        }

        const find = (nodes) => {
            for(const node of nodes)
            {
                if(!node) continue;

                if(Array.isArray(node))
                {
                    const found = find(node);
                    if (found) return found;
                }

                if(node.props?.value === selectedValue) return node;
            }
            return null;
        };

        const selectedChild = Array.isArray(children)
            ? find(children)
            : children?.props?.value === selectedValue
                ? children
                : null;

        if(!selectedChild)
        {
            return {
                value: defaultValue,
                name: defaultName,
                icon: defaultIcon
            };
        }

        return {
            value: selectedChild.props.value,
            name: selectedChild.props.name ?? defaultName,
            icon: selectedChild.props.icon ?? defaultIcon
        };
    }, [selectedValue, children, defaultValue, defaultName, defaultIcon]);


    return (
        <DropdownContext.Provider value={{currentSelectedValue, dropdownSelect}}>
            <StyledEditorToolbarTextDropdown>
                <div
                    className={isDropdownOpen ? "dropdownHeader isOpen" : "dropdownHeader"}
                    ref={dropdownHeaderRef}
                    onClick={() => setIsDropdownOpen(prev => !prev)}
                    style={maxWidth ? { width: `${maxWidth}px` } : undefined}
                >
                    <div className="iconContainer">
                        {currentSelectedValue.icon}
                    </div>
                    <div className="textContainer">
                        {currentSelectedValue.name}
                    </div>
                    <MaterialIcon
                        name='arrow_drop_down'
                        weight={300}
                        size='xs'
                    />
                </div>
                {
                    isDropdownOpen ? 
                    <div className="dropdownContent" ref={dropdownContentRef}>
                        {children}
                    </div>
                    : null
                }
                
                
            </StyledEditorToolbarTextDropdown>
        </DropdownContext.Provider>
        
    )

}

const StyledEditorToolbarTextDropdown = styled.div`
    position: relative;

    div.dropdownHeader
    {
        display: flex;
        align-items: center;
        gap: ${({theme}) => theme.spacing.sm};
        cursor: pointer;
        border-radius: ${({theme}) => theme.cornerRadius.sm};
        transition-duration: 110ms;
        padding: ${({theme}) => theme.spacing.xs};

        div.iconContainer
        {
            display: flex;
            align-items: center;
            justify-content: center;

            .icon
            {
                font-size: ${({theme}) => theme.iconSize.sm};
            }
        }

        div.textContainer
        {
            flex: 1;
        }

        &:hover, &.isOpen
        {
            background: rgba(${({theme}) => theme.colors.neutralPrimaryBorderRGB}, 0.5);
        }
    }

    div.dropdownContent
    {
        position: absolute;
        z-index: 2000;
        top: calc(100% + 3px);
        left: 50%;
        transform: translateX(-50%);
        background: ${({theme}) => theme.colors.neutralInputBackground};
        border-radius: ${({theme}) => theme.cornerRadius.sm};
        outline: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
    }
`

export const EditorToolbarTextDropdownOption = ({icon, name, value}) => {

    const { currentSelectedValue, dropdownSelect } = useContext(DropdownContext);

    const isSelected = currentSelectedValue.value === value;

    return (
        <StyledEditorToolbarTextDropdownOption
            onClick={() => dropdownSelect(value)}
            className={isSelected ? 'isActive' : null}
        >
            <div className="iconContainer">{icon}</div>
            <div>{name}</div>
        </StyledEditorToolbarTextDropdownOption>
    )
}

const StyledEditorToolbarTextDropdownOption = styled.div`
    display: flex;
    align-items: center;
    padding: ${({theme}) => theme.spacing.xs};
    cursor: pointer;
    gap: ${({theme}) => theme.spacing.sm};
    padding-right: calc(16px + ${({theme}) => theme.spacing.sm} + ${({theme}) => theme.spacing.xs});

    div.iconContainer
    {
        display: flex;
        align-items: center;
        justify-content: center;
        
        svg
        {
            height: 22px;
            width: 22px;
        }
    }

    > div
    {
        white-space: nowrap;
    }

    &:hover
    {
        background: rgba(${({theme}) => theme.colors.neutralPrimaryBorderRGB}, 0.5);
    }

    &.isActive
    {
        background: ${({theme}) => theme.colors.neutralPrimaryBorder};
    }
`

export const EditorToolbarColorDropdown = ({icon, defaultValue, selectedValue, onChange, highlight}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownContentRef = useRef(null);
    const dropdownHeaderRef = useRef(null);

    const dropdownSelect = (value) => {
        setIsDropdownOpen(false);
        onChange?.(value);
    };

    useEffect(() => {
        const headerEl = dropdownHeaderRef.current;
        const contentRef = dropdownContentRef.current;
        if(!headerEl || !contentRef) return;

        const clickHandler = (e) => {
            if(!e.target) return;

            if(!headerEl.contains(e.target) && !contentRef.contains(e.target)) setIsDropdownOpen(false);
        }

        document.addEventListener("click", clickHandler);

        return () => document.removeEventListener("click", clickHandler);
    }, [isDropdownOpen])

    const GlobalTheme = useTheme();

    const currentSelectedValue = (selectedValue ?? defaultValue) ?? "#FFFFFF00";

    
    const contrastColor = (hex) => {
        hex = hex.replace('#', '');

        const r = parseInt(hex.substr(0, 2), 16) / 255;
        const g = parseInt(hex.substr(2, 2), 16) / 255;
        const b = parseInt(hex.substr(4, 2), 16) / 255;

        const transform = (c) =>
            c <= 0.03928
            ? c / 12.92
            : Math.pow((c + 0.055) / 1.055, 2.4);

        const R = transform(r);
        const G = transform(g);
        const B = transform(b);

        const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;

        return luminance > 0.179 ? GlobalTheme.colors.neutralPrimaryForeground : GlobalTheme.colors.neutralPrimaryBackground;
    }

    const iconColor = highlight ? contrastColor(currentSelectedValue) : currentSelectedValue;
    const backgroundColor = highlight ? currentSelectedValue : null;
    
    return (
        <DropdownContext.Provider value={{currentSelectedValue, dropdownSelect}}>
            <StyledEditorToolbarColorDropdown
                $iconColor={iconColor}
                $backgroundColor={backgroundColor}
            >
                <div
                    ref={dropdownHeaderRef}
                    className="dropdownHeader"
                    onClick={() => setIsDropdownOpen(prev => !prev)}
                >
                    {icon}
                </div>

            {
                isDropdownOpen ?
                <div className="dropdownContent" ref={dropdownContentRef}>
                    <div className="colors">
                        {
                            editorColors.map((column, i) =>
                                <div key={i} className="colorColumn">
                                    {
                                        column.map(color => <EditorToolbarColorDropdownOption key={color} value={color}/>)
                                    }
                                </div>
                            )
                        }
                    </div>
                    <div className="resetColor"
                        onClick={() => dropdownSelect(null)}
                    >
                        <MaterialIcon name='ink_eraser' weight={300} size='sm'/>
                        Tilbakestill farge
                    </div>
                </div>
                : null
            }
                
                
            </StyledEditorToolbarColorDropdown>
        </DropdownContext.Provider>
        
    )

}

const StyledEditorToolbarColorDropdown = styled.div`
    position: relative;

    div.dropdownHeader
    {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border-radius: ${({theme}) => theme.cornerRadius.sm};
        transition-duration: 110ms;
        padding: ${({theme}) => theme.spacing.xs};
        background: ${({$backgroundColor}) => $backgroundColor};

        .icon
        {
            color: ${({$iconColor}) => $iconColor};
        }
    }

    div.dropdownContent
    {
        position: absolute;
        z-index: 2000;
        top: calc(100% + 3px);
        left: 50%;
        transform: translateX(-50%);
        background: ${({theme}) => theme.colors.neutralInputBackground};
        border-radius: ${({theme}) => theme.cornerRadius.sm};
        outline: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
        padding: ${({theme}) => theme.spacing.sm};

        div.colors
        {
            display: flex;
            align-items: top;
            justify-content: center;
            gap: ${({theme}) => theme.spacing.sm};
            padding-bottom: ${({theme}) => theme.spacing.md};

            div.colorColumn
            {
                display: flex;
                flex-direction: column;
                gap: ${({theme}) => theme.spacing.sm}
            }
        }

        div.resetColor
        {
            cursor: pointer;
            display: flex;
            align-items: center;
            white-space: nowrap;
            font-size: ${({theme}) => theme.fontSize.sm};
            gap: ${({theme}) => theme.spacing.sm};
            font-weight: 500;
            border-top: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
            padding-top: ${({theme}) => theme.spacing.md};

            .icon
            {
                color: ${({theme}) => theme.colors.mutedForeground};
            }
        }
    }
    
`

const EditorToolbarColorDropdownOption = ({value}) => {

    const {currentSelectedValue, dropdownSelect} = useContext(DropdownContext);

    return (
        <StyledEditorToolbarColorDropdownOption
            className={currentSelectedValue === value ? 'isSelected' : null}
            style={{background: value}}
            onClick={() => dropdownSelect(value)}
        />
    )
}

const StyledEditorToolbarColorDropdownOption = styled.button`
    height: 18px;
    width: 18px;
    cursor: pointer;
    border: none;
    outline: none;
    border-radius: ${({theme}) => theme.cornerRadius.sm};
    outline-offset: -1px;

    &:hover
    {
        outline: 1px solid rgba(${({theme}) => theme.colors.neutralPrimaryForegroundRGB}, 0.25);
    }

    &.isSelected
    {
        outline: 1px solid rgba(${({theme}) => theme.colors.neutralPrimaryForegroundRGB}, 0.5);
    }
`
